/**
 * @file useFileUpload.ts
 * @description Hook personalizado para gestionar la subida de archivos en React.
 * Proporciona una interfaz unificada para:
 * - Selección de archivos
 * - Validación de tamaño y tipo
 * - Gestión de estado (loading, errores)
 * - Manejo de eventos de subida
 * 
 * Este hook es la base del sistema de subida de archivos y está diseñado para ser:
 * - Reutilizable en diferentes contextos (chat, workflows, roles)
 * - Tipado con TypeScript
 * - Fácilmente extensible
 * - Independiente de la UI
 * 
 * @example
 * ```tsx
 * // Hook para gestionar la subida de archivos
 * // Proporciona funciones y estado para:
 * // - Seleccionar archivos
 * // - Validar tamaño/tipo
 * // - Manejar carga y errores
 * const {
 *   handleFileSelect,    // Maneja evento de selección
 *   triggerFileSelect,   // Abre diálogo de selección
 *   selectedFiles,       // Archivos seleccionados
 *   isLoading,          // Estado de carga
 *   error,              // Error si existe
 *   clearFiles          // Limpia selección
 * } = useFileUpload(
 *   // Callback cuando se seleccionan archivos
 *   async (files) => {
 *     await processFiles(files);
 *   },
 *   // Opciones de configuración
 *   { 
 *     maxSize: 5 * 1024 * 1024,  // Máximo 5MB
 *     accept: '.pdf,.doc',        // Solo PDF/DOC
 *     multiple: true,             // Múltiples archivos
 *     onError: console.error      // Manejo de errores
 *   }
 * );
 * ```
 * ```
 */

import { useState, useRef, useCallback, type ChangeEvent } from 'react';

/**
 * Opciones de configuración para el hook useFileUpload
 */
export interface FileUploadOptions {
  /** Tipos de archivo permitidos (ej: '.json', '.pdf') */
  accept?: string;
  /** Permite seleccionar múltiples archivos */
  multiple?: boolean;
  /** Tamaño máximo permitido en bytes (global, usado si no hay validador custom) */
  maxSize?: number;
  /** Validador custom para tamaño por tipo de archivo */
  customSizeValidator?: (file: File) => void;
  /** Validador custom para tipo de archivo */
  customTypeValidator?: (file: File) => void;
  /**
   * Validador custom del LOTE completo: número de ficheros, tamaño agregado… lo que no se puede
   * decidir mirando un fichero suelto.
   *
   * ⚠️ Recibe la lista YA COMBINADA (los ya seleccionados más los nuevos), porque eso es lo que
   * `ChatInput.handleDrop` mete en el evento sintético. Valida el acumulado, no sólo lo entrante:
   * si sólo miras lo nuevo, arrastrar de tres en tres se salta cualquier tope.
   */
  customBatchValidator?: (files: File[]) => void;
  /** Callback para manejar errores */
  onError?: (error: Error) => void;
}

/**
 * Resultado devuelto por el hook useFileUpload
 */
export interface FileUploadResult {
  /** Maneja el evento de selección de archivos */
  handleFileSelect: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  /** Activa programáticamente el selector de archivos */
  triggerFileSelect: () => void;
  /** Limpia los archivos seleccionados y el estado */
  clearFiles: () => void;
  /**
   * Repone una selección de ficheros. Existe para deshacer el `clearFiles()`
   * que se hace ANTES de enviar: si el envío falla —quedarse sin crédito, por
   * ejemplo— los adjuntos ya se habían vaciado y el usuario los perdía (#1299).
   */
  restoreFiles: (files: File[]) => void;
  /** Remueve un archivo específico de la selección */
  removeFile: (fileToRemove: File) => void;
  /** Lista de archivos seleccionados */
  selectedFiles: File[];
  /** Indica si hay una operación en curso */
  isLoading: boolean;
  /** Error actual si existe */
  error: Error | null;
  /** Referencia al input file */
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}

/**
 * Hook personalizado para manejar la subida de archivos
 * @param onFilesSelected - Callback que se ejecuta cuando se seleccionan archivos
 * @param options - Opciones de configuración
 */
export const useFileUpload = (
  onFilesSelected: (files: File[]) => Promise<void> | void,
  options: FileUploadOptions = {}
): FileUploadResult => {
  // Estados
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Referencia al input file
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Valida el tamaño de los archivos
   * Usa validador custom si está disponible, sino usa maxSize global
   */
  const validateFileSize = (files: File[]): void => {
    // If custom validator is provided, use it for each file
    if (options.customSizeValidator) {
      for (const file of files) {
        options.customSizeValidator(file);
      }
      return;
    }

    // Fallback to global maxSize validation
    if (!options.maxSize) return;

    const oversizedFiles = files.filter(file => file.size > options.maxSize!);
    if (oversizedFiles.length > 0) {
      const maxSizeMB = options.maxSize! / (1024 * 1024);
      throw new Error(
        oversizedFiles.length === 1
          ? `El archivo ${oversizedFiles[0].name} excede el tamaño máximo permitido de ${maxSizeMB}MB`
          : `${oversizedFiles.length} archivos exceden el tamaño máximo permitido de ${maxSizeMB}MB`
      );
    }
  };

  /**
   * Valida el lote completo (conteo, tamaño agregado) si hay validador custom.
   * Corre ANTES que las validaciones por fichero: si sobran ficheros, el usuario debe saberlo
   * antes de recibir un error sobre uno concreto.
   */
  const validateBatch = (files: File[]): void => {
    if (options.customBatchValidator) {
      options.customBatchValidator(files);
    }
  };

  /**
   * Valida el tipo de los archivos
   * Usa validador custom si está disponible
   */
  const validateFileType = (files: File[]): void => {
    if (options.customTypeValidator) {
      for (const file of files) {
        options.customTypeValidator(file);
      }
    }
  };

  /**
   * Maneja la selección de archivos
   */
  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      const files = Array.from(event.target.files || []);

      // Si no hay archivos, no hacemos nada
      if (files.length === 0) return;

      // Validar el lote completo primero (conteo, tamaño agregado)
      validateBatch(files);

      // Validar tipo de archivos (si hay validador custom)
      validateFileType(files);

      // Validar tamaño de archivos
      validateFileSize(files);

      // Actualizar estado
      setSelectedFiles(files);
      setIsLoading(true);
      setError(null);

      // Ejecutar callback del consumidor
      await onFilesSelected(files);

      // Limpiar input para permitir seleccionar el mismo archivo
      if (event.target) event.target.value = '';

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error desconocido al procesar archivos');
      setError(error);
      options.onError?.(error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Activa el selector de archivos programáticamente
   */
  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  /**
   * Limpia el estado y los archivos seleccionados
   */
  const clearFiles = useCallback(() => {
    setSelectedFiles([]);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  /**
   * Repone la selección tras un envío fallido. No revalida: son ficheros que ya
   * pasaron los validadores al elegirlos, y volver a rechazarlos aquí dejaría al
   * usuario sin adjuntos y sin explicación.
   */
  const restoreFiles = useCallback((files: File[]) => {
    if (!Array.isArray(files) || files.length === 0) return;
    setSelectedFiles(files);
  }, []);

  /**
   * Remueve un archivo específico de la selección
   */
  const removeFile = (fileToRemove: File) => {
    setSelectedFiles(prevFiles =>
      prevFiles.filter(file => file !== fileToRemove)
    );
  };

  return {
    handleFileSelect,
    triggerFileSelect,
    clearFiles,
    restoreFiles,
    removeFile,
    selectedFiles,
    isLoading,
    error,
    fileInputRef
  };
}; 